<template>
    <Head title="Report Issue" />
    <div class="flex flex-col min-h-screen overflow-y-auto h-screen bg-valence-neutral-light text-valence-grey-800 safe-area-top">
        <div class="fade-in grow justify-center gap-5 md:gap-8 flex flex-col items-center py-4 md:py-10">
            <div class="flex flex-col gap-4 justify-around text-center max-w-sm mx-auto px-4">
                <div class="flex flex-col pb-5 justify-center">
                    <img :src="Logo" alt="Valence logo" class="mx-auto h-16 w-auto" />
                </div>
            </div>

            <div class="w-full max-w-md px-4">
                <BasePageTitle title="Report an Issue" class="mb-6 text-center" />

                <template v-if="message">
                    <div role="status" class="rounded-lg bg-green-50 px-4 py-2 mb-6">
                        <p>{{ message }}</p>
                    </div>
                    <div class="flex justify-center mt-2">
                        <a :href="back_url" class="w-32 border py-4 px-5 rounded-lg leading-none text-center"> Back </a>
                    </div>
                </template>

                <form v-else class="flex flex-col gap-4" @submit.prevent="submitIssue">
                    <label for="subject" class="body-small flex flex-col gap-2">
                        Subject
                        <input v-model="form.subject" name="subject" type="text" class="border border-gray-300 rounded-md px-3 py-2" required />
                    </label>

                    <label v-if="!token" for="email" class="body-small flex flex-col gap-2">
                        Email
                        <input v-model="form.email" name="email" type="text" class="border border-gray-300 rounded-md px-3 py-2" />
                    </label>

                    <label for="body" class="body-small flex flex-col gap-2">
                        Description
                        <textarea v-model="form.body" name="body" class="border border-gray-300 rounded-md px-3 py-2 min-h-[120px]" required></textarea>
                    </label>

                    <div class="flex justify-between mt-2">
                        <a :href="back_url" class="w-32 border py-4 px-5 rounded-lg leading-none text-center"> Cancel </a>
                        <LoadingSpinner v-if="loading" class="w-10 h-10" />
                        <button
                            v-else
                            type="submit"
                            :disabled="!isFormValid"
                            class="w-32 border disabled:opacity-75 border-valence-pink shrink-0 hover:opacity-90 transition-opacity py-4 px-5 bg-valence-pink text-white rounded-lg leading-none justify-center">
                            Submit
                        </button>
                    </div>
                </form>
            </div>

            <div class="mt-auto flex flex-row justify-center">
                <a
                    rel="noopener"
                    href="https://www.valence.co/other/privacy"
                    target="_blank"
                    class="inline-flex items-center gap-1 hover:opacity-90 italic underline body-small px-4">
                    Valence Privacy Policy<span class="bi bi-box-arrow-up-right text-xs"></span>
                </a>
            </div>
        </div>
    </div>
</template>

<script setup>
import { getCookie } from "/js/utils.js";
import { Head, router, usePage } from "@inertiajs/vue3";
import { app, authentication } from "@microsoft/teams-js";
import { computedAsync } from "@vueuse/core";
import Logo from "~img/color-logo.svg";
import BasePageTitle from "~vue/components/BasePageTitle.vue";
import { useStatusBar } from "~vue/composables/useStatusBar";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { logError } from "~vue/utils/logUtils";
import { computed, ref } from "vue";

useStatusBar();

defineProps({
    message: {
        type: String,
        default: null,
    },
    back_url: {
        type: String,
        default: "/microsoft-teams/login/",
    },
});

const loading = ref(false);
const token = computedAsync(getClientSideToken);

const form = ref({
    subject: "",
    body: "",
});

const isFormValid = computed(() => {
    return form.value.subject.trim() !== "" && form.value.body.trim() !== "";
});

const postUrl = computed(() => usePage().url);

function submitIssue() {
    loading.value = true;
    router.post(postUrl.value, form.value, {
        headers: {
            "X-CSRFToken": getCookie("csrftoken"),
            "X-MST-Token": token.value,
        },
        onFinish: () => {
            loading.value = false;
        },
        only: ["message"],
    });
}

async function getClientSideToken() {
    try {
        app.initialize();
        return await authentication.getAuthToken();
    } catch (error) {
        logError(error);
    }
}
</script>
